import { useCallback, useState } from 'react';
import { INVENTORY_COLS, ITEMS } from '../../data/inventory.config';
import { useMenuNavigation } from '../../hooks/menu/useMenuNavigation';
import { useInventoryStore } from '../../store/inventorySlice';
import { dungeonService } from '../../services/DungeonService';
import { closeInterface } from '../../services/InterfaceService';
import { describeItemCard } from '../../services/CardDescriptionService';
import { Icon } from './Icon';
import { KeyDisplay } from './KeyDisplay';
import { ItemList, ItemListEntry } from './ItemList';
import { QuantityPopup } from './QuantityPopup';
import './Inventory.scss';

export const Inventory = () => {
  const slots = useInventoryStore((s) => s.slots);
  const gold = useInventoryStore((s) => s.gold);
  const removeItem = useInventoryStore((s) => s.removeItem);
  const sortByType = useInventoryStore((s) => s.sortByType);

  const [actionSlotIndex, setActionSlotIndex] = useState<number | null>(null);
  const [focusTargetIndex, setFocusTargetIndex] = useState<number | null>(null);
  const [throwSlotIndex, setThrowSlotIndex] = useState<number | null>(null);

  const inDungeon = dungeonService.getPlayer() !== null;

  const close = useCallback(() => {
    closeInterface();
  }, []);

  const closeAction = useCallback(() => {
    setFocusTargetIndex(actionSlotIndex);
    setActionSlotIndex(null);
  }, [actionSlotIndex]);

  const items: (ItemListEntry | null)[] = slots.map((slot, idx) => {
    const def = slot ? ITEMS[slot.itemId] : null;
    if (!slot || !def) {
      return null;
    }
    return {
      id: String(idx),
      navId: String(idx),
      icon: def.sprite,
      overlay: def.iconOverlay,
      quantity: def.maxStack > 1 ? slot.quantity : undefined,
      card: {
        icon: def.sprite,
        overlay: def.iconOverlay,
        name: def.label,
        subtitle: def.description,
        ...(describeItemCard(slot.itemId) ?? {}),
      },
    };
  });

  const actionSlot = actionSlotIndex !== null ? slots[actionSlotIndex] : null;
  const actionItem = actionSlot ? ITEMS[actionSlot.itemId] : null;
  const hasEffects = (actionItem?.effects?.length ?? 0) > 0;

  const filledCount = slots.filter(Boolean).length;
  const focusTarget =
    focusTargetIndex === null || filledCount === 0
      ? null
      : String(Math.min(focusTargetIndex, filledCount - 1));

  const { containerRef: actionMenuRef } = useMenuNavigation({
    disabled: actionSlotIndex === null,
    onConfirm: (id) => {
      if (!actionSlot || !actionItem) {
        return;
      }
      switch (id) {
        case 'use':
          if (hasEffects) {
            const used = dungeonService.inventoryService.useConsumable(
              actionSlot.itemId,
            );
            if (used) {
              removeItem(actionSlot.itemId, 1);
              dungeonService.inventoryService.triggerGroundPickup();
            }
          }
          setFocusTargetIndex(actionSlotIndex);
          setActionSlotIndex(null);
          break;
        case 'jeter': {
          const def = ITEMS[actionSlot.itemId];
          if (def && def.maxStack > 1 && actionSlot.quantity > 1) {
            setThrowSlotIndex(actionSlotIndex);
            setActionSlotIndex(null);
            break;
          }
          dungeonService.inventoryService.throwItem(actionSlot.itemId, 1);
          setFocusTargetIndex(actionSlotIndex);
          setActionSlotIndex(null);
          break;
        }
      }
    },
    onCancel: closeAction,
    onPause: close,
  });

  return (
    <div className="inventory-overlay">
      <div className="inventory-panel">
        <div className="inventory-header">
          <h2 className="inventory-title">Inventaire</h2>
          <div className="inventory-gold">
            <Icon
              sprite={ITEMS.gold.sprite}
              size={20}
              className="inventory-gold-icon"
            />
            <span className="inventory-gold-amount">{gold}</span>
          </div>
        </div>

        <div className="inventory-grid-wrapper">
          <ItemList
            active={actionSlotIndex === null && throwSlotIndex === null}
            items={items}
            cols={INVENTORY_COLS}
            focusTarget={focusTarget}
            onConfirm={(item) => {
              const index = parseInt(item.navId, 10);
              setTimeout(() => setActionSlotIndex(index), 0);
            }}
            onTertiary={sortByType}
            onCancel={close}
            onPause={close}
          />
        </div>

        <div className="inventory-hint">
          <KeyDisplay context="interface" action="CONFIRM" size="sm" /> Actions
          · <KeyDisplay context="interface" action="TERTIARY" size="sm" /> Trier
          · <KeyDisplay context="interface" action="CANCEL" size="sm" /> Retour
        </div>
      </div>

      {actionSlotIndex !== null && actionItem && (
        <div className="item-action-overlay">
          <div className="item-action-popup" ref={actionMenuRef}>
            <div className="item-action-title">{actionItem.label}</div>
            {actionItem.description && (
              <div className="item-action-desc">{actionItem.description}</div>
            )}
            <div className="item-action-buttons">
              {hasEffects && (
                <div
                  className="item-action-btn"
                  data-nav-row={0}
                  data-nav-col={0}
                  data-nav-id="use"
                >
                  Utiliser
                </div>
              )}
              {inDungeon && (
                <div
                  className="item-action-btn item-action-btn--danger"
                  data-nav-row={hasEffects ? 1 : 0}
                  data-nav-col={0}
                  data-nav-id="jeter"
                >
                  Jeter
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {throwSlotIndex !== null &&
        slots[throwSlotIndex] &&
        (() => {
          const slot = slots[throwSlotIndex];
          if (!slot) {
            return null;
          }
          return (
            <QuantityPopup
              title={`Jeter — ${ITEMS[slot.itemId]?.label ?? ''}`}
              max={slot.quantity}
              onConfirm={(amount) => {
                dungeonService.inventoryService.throwItem(slot.itemId, amount);
                setThrowSlotIndex(null);
                setFocusTargetIndex(throwSlotIndex);
              }}
              onCancel={() => setThrowSlotIndex(null)}
            />
          );
        })()}
    </div>
  );
};
